import { useState } from "react";
import { Pencil, Plus, Tags, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useCategories } from "@/contexts/CategoriesContext";
import { AddCategoryDialog, KIND_LABELS } from "@/components/categories/AddCategoryDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { logError } from "@/lib/log";

const TABS = [["all", "All"], ...Object.entries(KIND_LABELS)];
const KIND_COLORS = {
  income: "text-[#2E6F40] border-[#2E6F40]",
  expense: "text-[#D94F3D] border-[#D94F3D]",
  transfer: "text-[#6C7565] border-[#6C7565]",
  investment: "text-[#A67C00] border-[#A67C00]",
};

/** Category Management: table of all categories (4 type groups) with add/edit/delete. */
export default function Categories() {
  const { items, deleteCategory } = useCategories();
  const [tab, setTab] = useState("all");
  const [dialog, setDialog] = useState(null); // null | {mode:"add"} | {mode:"edit", cat}
  const [confirm, setConfirm] = useState(null);

  const rows = tab === "all" ? items : items.filter((c) => c.kind === tab);

  const remove = (cat) => setConfirm({
    title: `Delete "${cat.name}"?`,
    detail: "Existing transactions keep this name as plain text — only the category entry is removed.",
    run: async () => {
      try {
        await deleteCategory(cat.id);
        toast.success(`"${cat.name}" deleted`);
      } catch (e) {
        logError("Category delete failed", e);
        toast.error("Could not delete the category");
      }
    },
  });

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Settings · Taxonomy</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Categories.</h2>
        </div>
        <button
          data-testid="add-category-btn"
          onClick={() => setDialog({ mode: "add" })}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Add category
        </button>
      </div>

      <p className="text-sm text-[#6C7565] max-w-2xl -mt-2 flex items-start gap-2">
        <Tags className="w-4 h-4 mt-0.5 shrink-0" />
        Your categories power every dropdown — transactions, budgets, bills and recurring rules.
        Transfers and investments stay out of income &amp; expense totals in reports.
      </p>

      <div className="flex gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565] border-b border-[#D8D4C7] pb-3 flex-wrap" data-testid="category-tabs">
        {TABS.map(([v, label]) => (
          <button
            key={v}
            data-testid={`cat-tab-${v}`}
            onClick={() => setTab(v)}
            className={`pb-1 border-b-2 ${tab === v ? "border-[#1A1F16] text-[#1A1F16]" : "border-transparent hover:text-[#1A1F16]"}`}
          >{label}</button>
        ))}
        <span className="ml-auto" data-testid="category-count">{rows.length} categories</span>
      </div>

      <div className="border border-[#D8D4C7] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="categories-table">
          <thead>
            <tr className="border-b border-[#1A1F16] text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Type</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Category name</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-[#6C7565]">No categories in this group yet.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} className="border-b border-[#EAE7DD] hover:bg-[#FDFBF7]" data-testid={`category-row-${c.id}`}>
                <td className="px-4 py-3">
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${KIND_COLORS[c.kind]}`}>
                    {KIND_LABELS[c.kind]}
                  </span>
                </td>
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3 text-right">
                  <span className="flex justify-end gap-3">
                    <button data-testid={`edit-category-${c.id}`} title="Edit" onClick={() => setDialog({ mode: "edit", cat: c })} className="text-[#6C7565] hover:text-[#1A1F16]"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-category-${c.id}`} title="Delete" onClick={() => remove(c)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && (
        <AddCategoryDialog
          defaultKind={tab === "all" ? "expense" : tab}
          editing={dialog.mode === "edit" ? dialog.cat : null}
          onClose={() => setDialog(null)}
        />
      )}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
